import dayjs, { Dayjs } from 'dayjs';
import type { AllPlanScheduleType, CalendarEvent, MealType, ScheduledAction } from '@demind-inc/core';

import { safeLocalStorage } from '../utils/safeLocalStorage';

export type DailyDefaultKind = 'breakfast' | 'lunch' | 'dinner' | 'sleep';

export const DAILY_DEFAULTS_STORAGE_KEY = '@daily_defaults_plan_v1';
export const DAILY_DEFAULTS_UNDO_LIMIT = 2;
export const TONIGHT_SLEEP_START_HOUR = 18;

export type DailyDefaultsStore = {
  lastDailyDefaultsDate: string | null;
  lastPresentKinds: DailyDefaultKind[] | null;
  undoCounts: Record<DailyDefaultKind, number>;
};

export type DailyDefaultsResolveResult = {
  kinds: DailyDefaultKind[];
  planTypes: AllPlanScheduleType[];
  markDateDone: boolean;
  skipReasons: Partial<Record<DailyDefaultKind, string[]>>;
};

const MEAL_KINDS: DailyDefaultKind[] = ['breakfast', 'lunch', 'dinner'];
const DEFAULT_MEAL_LATEST_TIMES: Record<Exclude<DailyDefaultKind, 'sleep'>, string> = {
  breakfast: '10:30',
  lunch: '15:00',
  dinner: '21:00',
};

export const emptyDailyDefaultsStore = (): DailyDefaultsStore => ({
  lastDailyDefaultsDate: null,
  lastPresentKinds: null,
  undoCounts: { breakfast: 0, lunch: 0, dinner: 0, sleep: 0 },
});

const normalizeKindArray = (value: unknown): DailyDefaultKind[] | null => {
  if (!Array.isArray(value)) return null;
  return value.filter((kind): kind is DailyDefaultKind =>
    ['breakfast', 'lunch', 'dinner', 'sleep'].includes(String(kind))
  );
};

export const parseDailyDefaultsStore = (raw: string | null): DailyDefaultsStore => {
  if (!raw) return emptyDailyDefaultsStore();
  try {
    const parsed = JSON.parse(raw) as Partial<DailyDefaultsStore>;
    const counts = parsed.undoCounts ?? ({} as Record<DailyDefaultKind, number>);
    return {
      lastDailyDefaultsDate:
        typeof parsed.lastDailyDefaultsDate === 'string' ? parsed.lastDailyDefaultsDate : null,
      lastPresentKinds: normalizeKindArray(parsed.lastPresentKinds),
      undoCounts: {
        breakfast: Number.isFinite(counts.breakfast) ? counts.breakfast : 0,
        lunch: Number.isFinite(counts.lunch) ? counts.lunch : 0,
        dinner: Number.isFinite(counts.dinner) ? counts.dinner : 0,
        sleep: Number.isFinite(counts.sleep) ? counts.sleep : 0,
      },
    };
  } catch {
    return emptyDailyDefaultsStore();
  }
};

export const readDailyDefaultsStore = (): DailyDefaultsStore =>
  parseDailyDefaultsStore(safeLocalStorage.getItem(DAILY_DEFAULTS_STORAGE_KEY));

export const writeDailyDefaultsStore = (store: DailyDefaultsStore): void => {
  try {
    safeLocalStorage.setItem(DAILY_DEFAULTS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore storage failures
  }
};

export const isAfterHHmm = (now: Dayjs, hhmm?: string | null): boolean => {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return false;
  const [hourRaw, minuteRaw] = hhmm.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return false;
  const compare = now.hour(hour).minute(minute).second(0).millisecond(0);
  return now.isAfter(compare);
};

export const isPastTonightSleepPlanningTime = (now: Dayjs, sleepStartHHmm?: string | null) => {
  const hhmm = sleepStartHHmm || '23:00';
  const [hourRaw] = hhmm.split(':');
  const hour = Number(hourRaw);
  if (Number.isFinite(hour) && hour < TONIGHT_SLEEP_START_HOUR) {
    return now.hour() < TONIGHT_SLEEP_START_HOUR && isAfterHHmm(now, hhmm);
  }
  return isAfterHHmm(now, hhmm);
};

const normalizeText = (value?: string | null): string => String(value ?? '').trim().toLowerCase();

export const getDailyDefaultMealKindFromName = (name?: string | null): Exclude<DailyDefaultKind, 'sleep'> | null => {
  const normalized = normalizeText(name);
  if (normalized.includes('breakfast')) return 'breakfast';
  if (normalized.includes('lunch')) return 'lunch';
  if (normalized.includes('dinner')) return 'dinner';
  return null;
};

const isMealCalendarEvent = (event: CalendarEvent) =>
  event.scheduleFrom === 'meal' || event.autoScheduleFrom?.from === 'meal';

const isSleepCalendarEvent = (event: CalendarEvent) =>
  event.scheduleFrom === 'sleep' || event.autoScheduleFrom?.from === 'sleep';

export const isUpcomingNightSleepTiming = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  todayKey: string
): boolean => {
  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  if (!start?.isValid()) return false;

  if (start.isSame(todayKey, 'day') && start.hour() >= TONIGHT_SLEEP_START_HOUR) return true;
  if (
    start.isSame(dayjs(todayKey).add(1, 'day'), 'day') &&
    start.hour() < TONIGHT_SLEEP_START_HOUR
  ) {
    return true;
  }
  return !!end?.isValid() && start.isBefore(end) && start.isSame(todayKey, 'day');
};

const isUpcomingMealTiming = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  todayKey: string,
  now: Dayjs
): boolean => {
  const start = startDate ? dayjs(startDate) : null;
  if (!start?.isValid() || !start.isSame(todayKey, 'day')) return false;
  const end = endDate ? dayjs(endDate) : null;
  if (end?.isValid()) return end.isAfter(now);
  return !start.isBefore(now);
};

const hasUpcomingMealBlock = (
  calendarEvents: CalendarEvent[],
  pendingSchedules: ScheduledAction[],
  todayKey: string,
  now: Dayjs,
  discardedIds: string[]
): boolean => {
  const onCalendar = calendarEvents.some((event) => {
    if (event.status === 'cancelled' || !isMealCalendarEvent(event)) return false;
    return isUpcomingMealTiming(event.start?.date, event.end?.date, todayKey, now);
  });
  if (onCalendar) return true;

  return pendingSchedules.some((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    if (discardedIds.includes(id) || schedule.eventFrom !== 'meal') return false;
    return isUpcomingMealTiming(schedule.startDate, schedule.endDate, todayKey, now);
  });
};

const hasSleepTonightOnCalendar = (calendarEvents: CalendarEvent[], todayKey: string): boolean =>
  calendarEvents.some(
    (event) =>
      event.status !== 'cancelled' &&
      isSleepCalendarEvent(event) &&
      isUpcomingNightSleepTiming(event.start?.date, event.end?.date, todayKey)
  );

const hasSleepTonightInSchedules = (
  schedules: ScheduledAction[],
  todayKey: string,
  discardedIds: string[]
): boolean =>
  schedules.some((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    return (
      !discardedIds.includes(id) &&
      schedule.eventFrom === 'sleep' &&
      isUpcomingNightSleepTiming(schedule.startDate, schedule.endDate, todayKey)
    );
  });

const hasUpcomingSleepBlock = (
  calendarEvents: CalendarEvent[],
  pendingSchedules: ScheduledAction[],
  todayKey: string,
  discardedIds: string[]
): boolean =>
  hasSleepTonightOnCalendar(calendarEvents, todayKey) ||
  hasSleepTonightInSchedules(pendingSchedules, todayKey, discardedIds);

const isWorkoutCalendarEvent = (event: CalendarEvent) =>
  event.scheduleFrom === 'workout' || event.autoScheduleFrom?.from === 'workout';

const isStartAfterNow = (startISO: string | undefined | null, now: Dayjs): boolean => {
  if (!startISO) return false;
  const start = dayjs(startISO);
  return start.isValid() && start.isAfter(now);
};

/**
 * Matches lifestack_calendar `hasUpcomingMealCalendarEvent`: upcoming lunch/dinner
 * on the plan date (or any meal when planning a future day).
 */
const hasUpcomingMealOnPlanDate = (
  calendarEvents: CalendarEvent[],
  pendingSchedules: ScheduledAction[],
  planDateKey: string,
  now: Dayjs,
  discardedIds: string[]
): boolean => {
  const isFuturePlanDate = !now.isSame(planDateKey, 'day');

  const onCalendar = calendarEvents.some((event) => {
    if (event.status === 'cancelled' || !isMealCalendarEvent(event)) return false;
    if (!dayjs(event.start?.date).isSame(planDateKey, 'day')) return false;
    if (isFuturePlanDate) return true;
    const mealKind = getDailyDefaultMealKindFromName(event.summary);
    if (mealKind !== 'lunch' && mealKind !== 'dinner') return false;
    return isStartAfterNow(event.start?.date, now);
  });
  if (onCalendar) return true;

  return pendingSchedules.some((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    if (discardedIds.includes(id) || schedule.eventFrom !== 'meal') return false;
    if (!dayjs(schedule.startDate).isSame(planDateKey, 'day')) return false;
    if (isFuturePlanDate) return true;
    const mealKind = getDailyDefaultMealKindFromName(schedule.name);
    if (mealKind !== 'lunch' && mealKind !== 'dinner') return false;
    return isStartAfterNow(schedule.startDate, now);
  });
};

const isWorkoutOnPlanDate = (
  startDate: string | null | undefined,
  planDateKey: string
): boolean => {
  const start = startDate ? dayjs(startDate) : null;
  return !!start?.isValid() && start.isSame(planDateKey, 'day');
};

const hasWorkoutOnPlanDate = (
  calendarEvents: CalendarEvent[],
  pendingSchedules: ScheduledAction[],
  planDateKey: string,
  discardedIds: string[]
): boolean => {
  const onCalendar = calendarEvents.some(
    (event) =>
      event.status !== 'cancelled' &&
      isWorkoutCalendarEvent(event) &&
      isWorkoutOnPlanDate(event.start?.date, planDateKey)
  );
  if (onCalendar) return true;

  return pendingSchedules.some((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    if (discardedIds.includes(id) || schedule.eventFrom !== 'workout') return false;
    return isWorkoutOnPlanDate(schedule.startDate, planDateKey);
  });
};

/**
 * Meal / sleep / workout anchors already present for `planDateKey` (calendar or
 * pending all-plan schedules). Mirrors lifestack_calendar daily-defaults presence
 * checks so Plan My Day intro defaults leave those types unselected —
 * e.g. sleep tonight already on the calendar → sleep off.
 */
export const resolveExistingAnchorPlanTypes = ({
  planDateKey,
  now = dayjs(),
  calendarEvents,
  pendingSchedules = [],
  discardedIds = [],
}: {
  planDateKey: string;
  now?: Dayjs;
  calendarEvents: CalendarEvent[];
  pendingSchedules?: ScheduledAction[];
  discardedIds?: string[];
}): AllPlanScheduleType[] => {
  const existing: AllPlanScheduleType[] = [];
  if (hasUpcomingMealOnPlanDate(calendarEvents, pendingSchedules, planDateKey, now, discardedIds)) {
    existing.push('meal');
  }
  if (hasUpcomingSleepBlock(calendarEvents, pendingSchedules, planDateKey, discardedIds)) {
    existing.push('sleep');
  }
  if (hasWorkoutOnPlanDate(calendarEvents, pendingSchedules, planDateKey, discardedIds)) {
    existing.push('workout');
  }
  return existing;
};

const pushReason = (
  reasons: Partial<Record<DailyDefaultKind, string[]>>,
  kind: DailyDefaultKind,
  reason: string
) => {
  reasons[kind] = [...(reasons[kind] ?? []), reason];
};

export const planTypesForDailyDefaultKinds = (kinds: DailyDefaultKind[]): AllPlanScheduleType[] => {
  const types: AllPlanScheduleType[] = [];
  if (kinds.some((kind) => MEAL_KINDS.includes(kind)) && !types.includes('meal')) types.push('meal');
  if (kinds.includes('sleep')) types.push('sleep');
  return types;
};

export const resolveDailyDefaultsTypesToRun = ({
  todayKey,
  now,
  alreadyRanDate,
  lastPresentKinds: _lastPresentKinds,
  skipAllPlanning,
  includeTypes,
  disableMealAutoSchedule,
  mealLatestTimes,
  mealRecurringEnabled,
  sleepStartHHmm,
  undoCounts,
  calendarEvents,
  pendingSchedules,
  discardedIds,
  plannedTypes,
}: {
  todayKey: string;
  now: Dayjs;
  alreadyRanDate: string | null;
  lastPresentKinds?: DailyDefaultKind[] | null;
  skipAllPlanning: boolean;
  includeTypes?: { meal?: boolean; sleep?: boolean } | null;
  disableMealAutoSchedule?: boolean;
  mealLatestTimes?: Partial<Record<Exclude<DailyDefaultKind, 'sleep'>, string>>;
  mealRecurringEnabled?: Partial<Record<Exclude<DailyDefaultKind, 'sleep'>, boolean>>;
  sleepStartHHmm?: string | null;
  undoCounts: Record<DailyDefaultKind, number>;
  calendarEvents: CalendarEvent[];
  pendingSchedules: ScheduledAction[];
  discardedIds: string[];
  plannedTypes: AllPlanScheduleType[];
}): DailyDefaultsResolveResult => {
  const skipReasons: Partial<Record<DailyDefaultKind, string[]>> = {};
  const kinds: DailyDefaultKind[] = [];
  const upcomingMealBlock = hasUpcomingMealBlock(
    calendarEvents,
    pendingSchedules,
    todayKey,
    now,
    discardedIds
  );
  const upcomingSleepBlock = hasUpcomingSleepBlock(
    calendarEvents,
    pendingSchedules,
    todayKey,
    discardedIds
  );

  const evaluateKind = (kind: DailyDefaultKind) => {
    if (skipAllPlanning) pushReason(skipReasons, kind, 'skip_all_planning');
    if (undoCounts[kind] >= DAILY_DEFAULTS_UNDO_LIMIT) pushReason(skipReasons, kind, 'undo_opt_out');
    if (alreadyRanDate === todayKey) {
      pushReason(skipReasons, kind, 'already_ran_today');
    }

    if (kind === 'sleep') {
      if (includeTypes?.sleep === false) pushReason(skipReasons, kind, 'include_type_disabled');
      if (plannedTypes.includes('sleep')) pushReason(skipReasons, kind, 'already_planned');
      if (upcomingSleepBlock) pushReason(skipReasons, kind, 'upcoming_block');
      if (isPastTonightSleepPlanningTime(now, sleepStartHHmm)) pushReason(skipReasons, kind, 'time_of_day');
      return;
    }

    if (includeTypes?.meal === false) pushReason(skipReasons, kind, 'include_type_disabled');
    if (disableMealAutoSchedule) pushReason(skipReasons, kind, 'meal_auto_schedule_disabled');
    if (mealRecurringEnabled?.[kind] === false) pushReason(skipReasons, kind, 'recurring_disabled');
    if (upcomingMealBlock) pushReason(skipReasons, kind, 'upcoming_block');
    const latestTime = mealLatestTimes?.[kind] || DEFAULT_MEAL_LATEST_TIMES[kind];
    if (isAfterHHmm(now, latestTime)) pushReason(skipReasons, kind, 'time_of_day');
  };

  (['breakfast', 'lunch', 'dinner', 'sleep'] as DailyDefaultKind[]).forEach((kind) => {
    evaluateKind(kind);
    if (!(skipReasons[kind]?.length)) kinds.push(kind);
  });

  return {
    kinds,
    planTypes: planTypesForDailyDefaultKinds(kinds),
    markDateDone: kinds.length === 0,
    skipReasons,
  };
};

export const buildDailyDefaultsFocusAreas = (kinds: DailyDefaultKind[]): string[] => {
  const meals = kinds.filter((kind) => kind !== 'sleep');
  const focus: string[] = [];
  if (meals.length) {
    focus.push(`Only auto-place these meal anchors: ${meals.join(', ')}. Do not add other meals.`);
  }
  if (kinds.includes('sleep')) {
    focus.push("Auto-place tonight's sleep only. Do not add last night's sleep or tomorrow night.");
  }
  return focus;
};

export const filterDailyDefaultsCommitSchedules = (
  schedules: ScheduledAction[],
  todayKey: string,
  kinds: DailyDefaultKind[]
): ScheduledAction[] => {
  const kindSet = new Set(kinds);
  return schedules.filter((schedule) => {
    if (schedule.eventFrom === 'meal') {
      const mealKind = getDailyDefaultMealKindFromName(schedule.name);
      return !!mealKind && kindSet.has(mealKind) && !!schedule.startDate && dayjs(schedule.startDate).isSame(todayKey, 'day');
    }
    if (schedule.eventFrom === 'sleep') {
      return kindSet.has('sleep') && isUpcomingNightSleepTiming(schedule.startDate, schedule.endDate, todayKey);
    }
    return false;
  });
};

export const partitionDailyDefaultsRerunSchedules = (
  schedules: ScheduledAction[],
  todayKey: string,
  kinds: DailyDefaultKind[]
): {
  commitEntries: Array<{ id: string; schedule: ScheduledAction; kind: DailyDefaultKind }>;
  leftoverIdSet: Set<string>;
  schedulesToPersist: ScheduledAction[];
} => {
  const commitEntries = schedules.flatMap((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    const [commitSchedule] = filterDailyDefaultsCommitSchedules([schedule], todayKey, kinds);
    if (!commitSchedule) return [];
    const kind: DailyDefaultKind | null =
      schedule.eventFrom === 'sleep'
        ? 'sleep'
        : getDailyDefaultMealKindFromName(schedule.name);
    if (!kind) return [];
    return [{ id, schedule, kind }];
  });
  const commitIdSet = new Set(commitEntries.map(({ id }) => id));
  return {
    commitEntries,
    leftoverIdSet: new Set(
      schedules
        .map((schedule, index) => String(schedule.id ?? `plan-${index}`))
        .filter((id) => !commitIdSet.has(id))
    ),
    schedulesToPersist: commitEntries.map(({ schedule }) => schedule),
  };
};

export const buildDailyDefaultsToastMessage = (entries: Array<{ schedule: ScheduledAction; kind: DailyDefaultKind }>): string => {
  const parts = entries.map(({ schedule, kind }) => {
    const time = schedule.startDate ? dayjs(schedule.startDate).format('HH:mm') : null;
    const label = kind === 'sleep' ? 'sleep' : kind;
    return time ? `${label} at ${time}` : label;
  });
  if (!parts.length) return '';
  return `Added ${parts.join(' and ')}`;
};

export const mealTypesForDailyDefaultKinds = (kinds: DailyDefaultKind[]): MealType[] =>
  kinds.filter((kind): kind is MealType => ['breakfast', 'lunch', 'dinner'].includes(kind));

/**
 * Remove leftover meal/sleep extras from a defaults rerun without dropping
 * cached todo/routine/workout cards, even if those ids were marked leftover.
 */
export const filterLiveSchedulesAfterDailyDefaultsRerun = (
  liveSchedules: ScheduledAction[],
  leftoverIdSet: Set<string>
): ScheduledAction[] =>
  liveSchedules.filter((schedule, index) => {
    const id = String(schedule.id ?? `plan-${index}`);
    if (!leftoverIdSet.has(id)) return true;
    return schedule.eventFrom !== 'meal' && schedule.eventFrom !== 'sleep';
  });
